import React, { useState } from 'react';
import { LogIn, UserPlus } from 'lucide-react';
import { useAuth } from '../lib/AuthContext';
import { useTranslation } from '../lib/i18n';

const Auth = () => {
  const { signIn, signUp } = useAuth();
  const { t } = useTranslation();
  const [mode, setMode] = useState('signin'); // 'signin' | 'signup'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    // Close the keyboard before the app shell mounts. On iOS the webview
    // keeps the shrunken viewport otherwise, and the first screen renders
    // under the status bar until the next relaunch.
    try {
      document.activeElement?.blur();
    } catch {
      /* not fatal */
    }
    setError('');
    setInfo('');
    setBusy(true);
    try {
      if (mode === 'signin') {
        await signIn(email, password);
      } else {
        const data = await signUp(email, password);
        if (!data?.session) {
          setInfo(t('auth.confirmEmail'));
          setMode('signin');
        }
      }
    } catch (err) {
      setError(err?.message || t('auth.failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-sm border border-gray-200 p-8 space-y-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900">{t('app.title')}</h1>
          <p className="text-sm text-gray-500 mt-1">
            {mode === 'signin' ? t('auth.signInTitle') : t('auth.signUpTitle')}
          </p>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('auth.email')}</label>
            <input
              type="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('auth.password')}</label>
            <input
              type="password"
              required
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </div>
          )}
          {info && (
            <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
              {info}
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            className="w-full inline-flex items-center justify-center gap-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white font-medium py-2 rounded-lg transition-colors"
          >
            {mode === 'signin' ? <LogIn size={16} /> : <UserPlus size={16} />}
            {busy
              ? t('auth.pleaseWait')
              : mode === 'signin'
              ? t('auth.signIn')
              : t('auth.signUp')}
          </button>
        </form>

        <div className="text-center text-sm text-gray-500">
          {mode === 'signin' ? (
            <>
              {t('auth.noAccount')}{' '}
              <button
                onClick={() => {
                  setMode('signup');
                  setError('');
                  setInfo('');
                }}
                className="text-primary-600 hover:underline"
              >
                {t('auth.signUp')}
              </button>
            </>
          ) : (
            <>
              {t('auth.haveAccount')}{' '}
              <button
                onClick={() => {
                  setMode('signin');
                  setError('');
                  setInfo('');
                }}
                className="text-primary-600 hover:underline"
              >
                {t('auth.signIn')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Auth;
